import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  untracked,
  viewChildren,
} from '@angular/core';
import { QuickSearchFacadeService } from '@fc-search/services/quick-search-facade.service';
import { QsVehicleResultComponent } from './quick-search-results/qs-vehicle-result.component';
import { QsEmployeeResultComponent } from './quick-search-results/qs-employee-result.component';
import { QsTaskResultComponent } from './quick-search-results/qs-task-result.component';
import { QsLocationResultComponent } from './quick-search-results/qs-location-result.component';
import { QsContactResultComponent } from './quick-search-results/qs-contact-result.component';
import { QsCompanyResultComponent } from './quick-search-results/qs-company-result.component';
import { QsChannelResultComponent } from './quick-search-results/qs-channel-result.component';
import { QsWorkflowResultComponent } from './quick-search-results/qs-workflow-result.component';

@Component({
  selector: 'fc-quick-search-recent',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    QsVehicleResultComponent,
    QsEmployeeResultComponent,
    QsTaskResultComponent,
    QsLocationResultComponent,
    QsContactResultComponent,
    QsCompanyResultComponent,
    QsChannelResultComponent,
    QsWorkflowResultComponent,
  ],
  template: `
    <div
      class="md:max-h-[400px] md:overflow-y-auto"
      (click)="facade.clearAndClose()"
    >
      <div class="px-16 py-8 text-xs font-medium text-secondary-400">
        Recently viewed
      </div>
      @for (
        item of facade.recentItems();
        track item.entityType + item.obj.id;
        let i = $index
      ) {
        <div #recentItem class="relative" (click)="recordOriginal(i)">
          @switch (item.entityType) {
            @case ('vehicle_vehicle') {
              <fc-qs-vehicle-result
                #resultComp
                [result]="item"
                [selected]="facade.selectedIndex() === i"
                [activeButtonIndex]="
                  facade.selectedIndex() === i
                    ? facade.selectedButtonIndex()
                    : -1
                "
              />
            }
            @case ('authentication_employee') {
              <fc-qs-employee-result
                #resultComp
                [result]="item"
                [selected]="facade.selectedIndex() === i"
                [activeButtonIndex]="
                  facade.selectedIndex() === i
                    ? facade.selectedButtonIndex()
                    : -1
                "
              />
            }
            @case ('task_task') {
              <fc-qs-task-result
                #resultComp
                [result]="item"
                [selected]="facade.selectedIndex() === i"
                [activeButtonIndex]="
                  facade.selectedIndex() === i
                    ? facade.selectedButtonIndex()
                    : -1
                "
              />
            }
            @case ('task_workflow') {
              <fc-qs-workflow-result
                #resultComp
                [result]="item"
                [selected]="facade.selectedIndex() === i"
                [activeButtonIndex]="
                  facade.selectedIndex() === i
                    ? facade.selectedButtonIndex()
                    : -1
                "
              />
            }
            @case ('poi_location') {
              <fc-qs-location-result
                #resultComp
                [result]="item"
                [selected]="facade.selectedIndex() === i"
                [activeButtonIndex]="
                  facade.selectedIndex() === i
                    ? facade.selectedButtonIndex()
                    : -1
                "
              />
            }
            @case ('crm_contact') {
              <fc-qs-contact-result
                #resultComp
                [result]="item"
                [selected]="facade.selectedIndex() === i"
                [activeButtonIndex]="
                  facade.selectedIndex() === i
                    ? facade.selectedButtonIndex()
                    : -1
                "
              />
            }
            @case ('crm_company') {
              <fc-qs-company-result
                #resultComp
                [result]="item"
                [selected]="facade.selectedIndex() === i"
                [activeButtonIndex]="
                  facade.selectedIndex() === i
                    ? facade.selectedButtonIndex()
                    : -1
                "
              />
            }
            @case ('chat_channel') {
              <fc-qs-channel-result
                #resultComp
                [result]="item"
                [selected]="facade.selectedIndex() === i"
                [activeButtonIndex]="
                  facade.selectedIndex() === i
                    ? facade.selectedButtonIndex()
                    : -1
                "
              />
            }
          }
        </div>
      }
    </div>
  `,
})
export class QuickSearchRecentComponent {
  facade = inject(QuickSearchFacadeService);
  active = input(true);

  private recentElements = viewChildren<ElementRef>('recentItem');
  private resultComps = viewChildren<{ activateButton(index: number): void }>(
    'resultComp',
  );

  constructor() {
    afterRenderEffect(() => {
      const idx = this.facade.selectedIndex();
      if (idx >= 0) {
        this.recentElements()[idx]?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    });

    let lastActivation = 0;
    effect(() => {
      const activation = this.facade.buttonActivated();
      if (activation > lastActivation) {
        lastActivation = activation;
        if (!untracked(() => this.active())) return;
        const idx = untracked(() => this.facade.selectedIndex());
        const btnIdx = untracked(() => this.facade.selectedButtonIndex());
        if (idx >= 0 && btnIdx >= 0) {
          const comp = untracked(() => this.resultComps()[idx]);
          comp?.activateButton?.(btnIdx);
        }
      }
    });
  }

  recordOriginal(index: number): void {
    this.facade.trackItemOpened(this.facade.recentItems()[index]);
  }
}
